import React, { forwardRef, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import cn from "classnames";
import sass from "pages/StepPages/StepFour/Two/Calculate/calculate.module.sass";
import ModalPortal from "components-new/ModalPortal";
import InputTemp from "components-new/InputTemp";
import { Button } from "components/shared";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "components/shared/Button/Button";
import { IFormulasState } from "./index";
import { generateIndex, prettify } from "pages/StepPages/StepFour/Two/Calculate/helpers";
import { organizationAPI } from "libs/apiClient/Organization";

interface IRow {
	change: (data: any) => void;
	removeField?: boolean;
	disable?: boolean;
	addIcon?: boolean;
	data: IFormulasState;
	label?: string;
	location: number;
}

/**
 * Компонент отрисовывает формулу с вложенными полями для заполнения
 * принимает:
 * 1) addIcon - отображать ли иконку добавления строк
 * 2) change - по разблюриванию последней пустой формулы происходит отправка данных на сервер,
 * результат расчета передается через эту функцию родителю
 * 3) removeField - функция которая, если передана, отображает иконку удаления, отвечает за удаление полей формулы
 * 4) disable - состояния отключенного таба формулы
 * 6) data - сама формула
 * 7) label - подпись формулы
 * 8) location - для отправки формы для конкретного адреса
 */
export const Row = forwardRef<any, IRow>(
	({ addIcon = false, change, location, removeField = false, label, data, disable = false }, ref) => {
		/**
		 * Состояние формулы, открыта\закрыта
		 */
		const [open, setOpen] = useState(false);

		/**
		 * Формула
		 */
		const [field, setField] = useState<IFormulasState>();

		/**
		 * Данные из URL
		 */
		const params = useParams();

		/**
		 * Если формула от родителя передана устанавливается в локальное состояние
		 */
		useEffect(() => {
			setField(data);
		}, [data]);

		/**
		 * Если формула от родителя передана устанавливается в локальное состояние
		 */
		const keyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (
				(e.key === "." || e.key === ",") &&
				(e.currentTarget.value.indexOf(".") !== -1 || e.currentTarget.value.indexOf(",") !== -1)
			) {
				e.preventDefault();
			}
			if (
				e.key !== "Backspace" &&
				e.currentTarget.value.length === 1 &&
				/^0/.test(e.currentTarget.value) &&
				/\d/.test(e.key)
			) {
				e.currentTarget.value = 0 + ".";
			}
		};

		/**
		 * Функция отправки данных по разфокусу
		 */
		const onBlur = (obj: IFormulasState) => {
			const flag = obj.fields.every((_) => {
				return _.value;
			});
			if (flag && params.threadId) {
				organizationAPI
					.objectDamageCreate(
						+params.threadId!,
						{ type: obj.type as any },
						{
							id: location,
							values: obj.fields.map((_) => {
								return Number(_.value.replace(",", "."));
							}),
						},
					)
					.then((r) => {
						change(r.data);
					});
			}
			// }
		};

		/**
		 * Состояние модалки подтверждения удаления поля формулы
		 */
		const [confirm, setConfirm] = useState<{ active: boolean; index: number | null }>({ active: false, index: null });

		/**
		 * Прослушиватель события ввода значений в поле,
		 * все буквы удаляются до того как попадут в локальное состояние
		 */
		const changeField = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, label: string) => {
			let value;
			value = evt.target.value.replace(/[^0-9.,]/g, "").replace(/\./g, ",");
			if (/^\./.test(value)) {
				value = 0 + value;
			}
			evt.target.value = value;
			setField(
				(prevState) =>
					prevState && {
						...prevState,
						fields: [...prevState.fields].map((field) =>
							field.label === label
								? {
										...field,
										value: evt.target.value,
								  }
								: field,
						),
					},
			);
		};

		/**
		 * Прослушиватель события ввода в модалке добавления дополнительных полей
		 * Все буквы вырезаются
		 * Максимально возможное значение 300
		 */
		const changeAddedField = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			let value;
			value = evt.target.value.replace(/[^0-9]/g, "");
			if (Number(value) > 300) {
				value = "300";
			}
			evt.target.value = value;
			setAddedFields(evt.target.value);
		};

		/**
		 * Состояние модалки добавления полей формулы
		 */
		const [modal, setModal] = useState(false);

		/**
		 * Состояние для ввода количества добавленных полей
		 */
		const [addedFields, setAddedFields] = useState("");

		/**
		 * Функция добавления в формулу добавленных полей, вешается на кнопку добавить
		 */
		const save = () => {
			if (field) {
				let addedArray = Array(Number(addedFields))
					.fill({ label, value: "" })
					.map((_, index) => ({ ..._, label: `${_.label} ${index + 1}` }));
				addedArray = addedArray.map((_, index) =>
					field.fields[index] && field.fields[index].label === _.label ? { ...field.fields[index] } : { ..._ },
				);
				setField(
					(prevState) =>
						prevState && {
							...prevState,
							fields: addedArray,
						},
				);
				setModal(false);
				setOpen(true);
				setAddedFields("");
			}
		};

		/**
		 * Функция удаления добавленных формул
		 */
		const deleteField = (index: number) => {
			if (field) {
				if (field.fields.length !== 1) {
					let newFields = [...field.fields];
					newFields.splice(index, 1);
					setAddedFields(String(newFields.length));
					setField(
						(prevState) =>
							prevState && {
								...prevState,
								fields: newFields,
							},
					);
					setConfirm({ active: false, index: null });
					onBlur({ ...field, fields: newFields });
				}
			}
		};

		/**
		 * Эффект следит за формулой и предзаполняет строку добавления поле формулы
		 * в зависимости от того сколько там уже есть полей
		 */
		useEffect(() => {
			field && setAddedFields(String(field.fields.length));
		}, [field]);

		/**
		 * В зависимости от типа формулы возвращает значение ≈, для того чтобы показать что результат примерный,
		 * а так же постфикс результата
		 */
		const indexes = field && generateIndex(field.type);

		const clickTab = (e: React.MouseEvent<HTMLDivElement>) => {
			const target = e.currentTarget;
			if (disable || !field?.fields.length) {
				return;
			} else {
				const newState = !open;
				setOpen(newState);
				if (newState) {
					if (target) {
						setTimeout(() => {
							target.parentElement!.scrollIntoView({ block: "center", behavior: "smooth" });
						}, 200);
					}
				}
			}
		};

		return (
			<div
				ref={ref}
				className={cn(sass.tab_wrap, disable ? sass.tab_wrap_disable : !field?.fields.length && sass.tab_wrap_empty)}>
				{modal && label && (
					<ModalPortal outSide={false} title={"Задать количество полей"} handleClose={() => setModal(false)}>
						<div className={sass.modal}>
							<InputTemp value={addedFields} onChange={changeAddedField} label={`Кол-во полей ${label}`} />
							<div className={sass.btns}>
								<Button variant={BUTTON_VARIANTS.OUTLINED} onClick={() => setModal(false)}>
									Отменить
								</Button>
								<Button onClick={save} disabled={field && Number(addedFields) === field.fields.length}>
									Готово
								</Button>
							</div>
						</div>
					</ModalPortal>
				)}
				{confirm.active && (
					<ModalPortal
						outSide={false}
						title={"Удалить поле"}
						handleClose={() => setConfirm({ active: false, index: null })}>
						<div className={sass.btns}>
							<Button variant={BUTTON_VARIANTS.OUTLINED} onClick={() => setConfirm({ active: false, index: null })}>
								Отменить
							</Button>
							<Button
								onClick={() => {
									deleteField(confirm.index!);
								}}
								color={BUTTON_COLORS.ERROR}>
								Готово
							</Button>
						</div>
					</ModalPortal>
				)}
				<div
					onClick={clickTab}
					className={cn(sass.tab_title_block, sass[`tab_title_block-${open ? "open" : "close"}`])}>
					<div className={sass.tab_title}>
						<span>{field?.name}</span>
						<span>{field?.description}</span>
					</div>
					<div className={sass.tab_icons}>
						{addIcon && (
							<svg
								onClick={(e) => {
									e.stopPropagation();
									!disable && setModal(true);
								}}
								className={sass.add}
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<rect x="7" width="2" height="16" fill="#005B9B" />
								<rect x="16" y="7" width="2" height="16" transform="rotate(90 16 7)" fill="#005B9B" />
							</svg>
						)}
						<div className={sass.tab_arrow}>
							<svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M8 0L0 8.09717L1.88 10L8 3.81916L14.12 10L16 8.09717L8 0Z" fill="#949494" />
							</svg>
						</div>
					</div>
				</div>
				<div className={sass.tab_block}>
					<div className={sass.tab_fields}>
						{field?.fields.map((_, index) => {
							const newLabel = /\s\d+$/.test(_.label) ? _.label.replace(/\d+$/g, String(index + 1)) : _.label;
							return (
								<div className={sass.field_box} key={index}>
									<div className={sass.field}>
										<InputTemp
											label={newLabel}
											onChange={(e) => changeField(e, _.label)}
											onKeyDown={keyDown}
											value={_.value}
										/>
										{removeField && (
											<div onClick={() => setConfirm({ active: true, index })} className={sass.svg}>
												<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
													<g clipPath="url(#clip0_6036_105168)">
														<path
															d="M9.70499 9.00009L13.85 4.85509C13.9319 4.75944 13.9747 4.6364 13.9698 4.51057C13.965 4.38473 13.9128 4.26536 13.8238 4.17631C13.7347 4.08726 13.6154 4.0351 13.4895 4.03024C13.3637 4.02538 13.2406 4.06818 13.145 4.15009L8.99999 8.29509L4.85499 4.14509C4.76083 4.05094 4.63314 3.99805 4.49999 3.99805C4.36684 3.99805 4.23914 4.05094 4.14499 4.14509C4.05083 4.23924 3.99794 4.36694 3.99794 4.50009C3.99794 4.63324 4.05083 4.76094 4.14499 4.85509L8.29499 9.00009L4.14499 13.1451C4.09265 13.1899 4.05014 13.2451 4.02012 13.3071C3.99011 13.3691 3.97325 13.4367 3.97059 13.5056C3.96793 13.5744 3.97953 13.6431 4.00467 13.7073C4.0298 13.7714 4.06793 13.8297 4.11666 13.8784C4.16539 13.9271 4.22366 13.9653 4.28783 13.9904C4.35199 14.0155 4.42065 14.0272 4.48951 14.0245C4.55837 14.0218 4.62594 14.005 4.68797 13.975C4.75 13.9449 4.80516 13.9024 4.84999 13.8501L8.99999 9.70509L13.145 13.8501C13.2406 13.932 13.3637 13.9748 13.4895 13.9699C13.6154 13.9651 13.7347 13.9129 13.8238 13.8239C13.9128 13.7348 13.965 13.6155 13.9698 13.4896C13.9747 13.3638 13.9319 13.2407 13.85 13.1451L9.70499 9.00009Z"
															fill="#949494"
														/>
													</g>
													<defs>
														<clipPath id="clip0_6036_105168">
															<rect width="18" height="18" fill="white" />
														</clipPath>
													</defs>
												</svg>
											</div>
										)}
									</div>
									<p className={sass.footer}>
										{index === 0 && field.type === "D_VSO"
											? "снижение выплат (отчислений) i-й сторонней организации в бюджеты РФ, " +
											  "возникающее вследствие прекращения или нарушения функционирования рассматриваемого объекта КИИ, " +
											  "исчисляется в рублях"
											: index === 0 && field.type === "B_MIDDLE"
											? "прогнозируемый общий объем дохода федерального бюджета РФ за i-й год, исчисляется в рублях"
											: _.footer}
									</p>
								</div>
							);
						})}
					</div>
					<div className={sass.wrapper_result}>
						<Button onClick={() => field && onBlur(field)} className={sass.button}>
							Рассчитать
						</Button>
						<div className={sass.tab_result}>
							<span>Результат</span>
							<span>
								{indexes && indexes.simple}
								{field ? prettify(field.result) : 0}
								{indexes && indexes.endOf}
							</span>
						</div>
					</div>
				</div>
			</div>
		);
	},
);
